import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for client-side error tracking
 * Call this function as early as possible in the application lifecycle
 */
export function initSentry() {
    // Check if Sentry is enabled
    const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED !== "false";
    const sentryDsn = import.meta.env.VITE_SENTRY_DSN_CLIENT;

    if (!sentryEnabled) {
        console.log("[Sentry] Disabled via environment variable");
        return;
    }

    if (!sentryDsn) {
        console.warn("[Sentry] DSN not configured. Error tracking disabled.");
        return;
    }

    Sentry.init({
        dsn: sentryDsn,

        // Environment (development, staging, production)
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || "development",

        // Release version for tracking which version errors occur in
        release: import.meta.env.VITE_SENTRY_RELEASE,

        // Trace propagation targets for distributed tracing
        tracePropagationTargets: [
            "localhost",
            /^\//,
            // Add your production domain here
            // /^https:\/\/your-domain\.com/
        ],

        // Integrations
        integrations: [
            // Browser tracing for performance monitoring
            Sentry.browserTracingIntegration(),
            Sentry.consoleLoggingIntegration({
                levels: [
                    "warn",
                    "error",
                ],
            }),

            // No replayIntegration: session replay is disabled per
            // remote_privacy_policy.md. It was previously loaded with both
            // sample rates at 0, which captured nothing but still shipped
            // and initialised the rrweb recorder. Removing the integration
            // makes "disabled" a structural fact rather than a sampling
            // setting one config change away from being live.
        ],

        // Sample rates
        // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring
        // We recommend adjusting this value in production
        tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "0.1"),

        // Capture 100% of errors by default
        sampleRate: parseFloat(import.meta.env.VITE_SENTRY_ERROR_SAMPLE_RATE || "1.0"),

        // Belt and braces: even if replayIntegration is reintroduced by
        // accident, these keep it from recording.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,

        // Before send hook - modify or filter events before sending to Sentry
        beforeSend(event, _hint) {
            // Don't send events in development unless explicitly enabled
            if (import.meta.env.MODE === "development" && import.meta.env.VITE_SENTRY_DEBUG !== "true") {
                console.log("[Sentry] Event captured (not sent in development):", event);
                return null;
            }

            // Privacy: Scrub any potential PII from events
            if (event.user) {
                // Remove email and other PII, keep only user ID
                delete event.user.email;
                delete event.user.username;
                delete event.user.ip_address;
            }

            // Scrub email addresses from error messages and context
            const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

            if (event.message) {
                event.message = event.message.replace(emailRegex, "[EMAIL_REDACTED]");
            }

            if (event.exception?.values) {
                event.exception.values.forEach((exception) => {
                    if (exception.value) {
                        exception.value = exception.value.replace(emailRegex, "[EMAIL_REDACTED]");
                    }
                });
            }

            return event;
        },

        // Enable debug mode in development
        debug: import.meta.env.MODE === "development",
    });

    console.log("[Sentry] Initialized successfully");
}

/**
 * Set user context in Sentry
 * Call this when user logs in
 * Note: Only user ID is tracked for privacy. Email is intentionally excluded.
 */
export function setSentryUser(user: { id: string }) {
    Sentry.setUser({
        id: user.id,
    });
}

/**
 * Clear user context in Sentry
 * Call this when user logs out
 */
export function clearSentryUser() {
    Sentry.setUser(null);
}

/**
 * Manually capture an exception
 */
export function captureException(error: Error, context?: Record<string, any>) {
    if (context) {
        Sentry.withScope((scope) => {
            Object.entries(context).forEach(([key, value]) => {
                scope.setContext(key, value);
            });
            Sentry.captureException(error);
        });
    } else {
        Sentry.captureException(error);
    }
}

/**
 * Add a breadcrumb for debugging
 */
export function addBreadcrumb(message: string, data?: Record<string, any>) {
    Sentry.addBreadcrumb({
        message,
        data,
        level: "info",
        timestamp: Date.now() / 1000,
    });
}
